import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  showModal,
} from '@edna/components';

import {Message} from 'src/components';
import {ModalCenterImage, Text} from 'src/components/primitives';
import {useGetBalanceQuery} from 'src/containers/Billing';
import {CongratsImage} from 'src/images';
import {joiResolver} from 'src/utils';

// eslint-disable-next-line no-restricted-imports
import {useRefundPayKeeperPaymentMutation} from '../../api';
import {TPayKeeperPayment} from '../definitions';
import * as S from '../style';

type TModalProps = {
  closeModal: TEmptyFunction;
};

const SuccessModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();

  return (
    <Modal width="430px">
      <ModalHeader>{t('Corrections:payments.card.successRefundModal.title')}</ModalHeader>
      <Text secondary>{t('Corrections:payments.card.successRefundModal.subtitle')}</Text>
      <ModalCenterImage as={CongratsImage} />
      <ModalActions>
        <ModalSubmitButton onClick={closeModal}>{t('Common:button.ok')}</ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

SuccessModal.displayName = 'SuccessModal';

const showSuccessModal = () => showModal((closeModal) => <SuccessModal closeModal={closeModal} />);

const RefundModal = React.memo<TModalProps & {isPartial?: boolean; item: TPayKeeperPayment}>(
  ({closeModal, item, isPartial}) => {
    const {t} = useTranslation();

    const {data: balance} = useGetBalanceQuery(item.tenantId);
    const [refund, {isLoading}] = useRefundPayKeeperPaymentMutation();

    const methods = useForm({
      mode: 'all',
      resolver: joiResolver((joi) =>
        joi.object({
          sum: isPartial ? joi.number().min(1).max(item.sum).required() : joi.any(),
        }),
      ),
    });

    const sum = methods.watch('sum');

    const canRefund = React.useMemo(() => {
      if (isPartial && !sum) {
        return true;
      }

      if (!balance || !balance?.amount) {
        return false;
      }

      const refSum = isPartial ? Number(sum) : item.sum;

      return balance.amount >= refSum;
    }, [balance, isPartial, sum]);

    const handleSubmit = React.useCallback(() => {
      refund({orderId: item.id, userId: item.userId, refundAmount: Number(sum || item.sum)})
        .unwrap()
        .then(showSuccessModal)
        .catch(() => {});

      closeModal();
    }, [closeModal, item, sum]);

    return (
      <FormProvider {...methods}>
        <Modal width="582px">
          <ModalHeader>
            {t(`Corrections:payments.card.refundModal.${isPartial ? 'partialTitle' : 'title'}`)}
          </ModalHeader>
          {!canRefund && (
            <Message type="Error" shadowSize="XS" fontSize="S">
              {t('Corrections:payments.card.refundModal.balanceError')}
            </Message>
          )}
          {!isPartial && (
            <>
              <Text bottomIndent="4">{t('Corrections:payments.card.refundModal.check')}</Text>
              <Text>
                <Text isInline secondary rightIndent="1">
                  {t('Corrections:payments.card.table.sum')}:
                </Text>
                {item.sum}
              </Text>
            </>
          )}
          {isPartial && (
            <>
              <S.SmallLabel htmlFor="sum">
                {t('Corrections:payments.card.refundModal.sum')}
              </S.SmallLabel>
              <S.RefundNumberInput name="sum" />
            </>
          )}
          <Text>
            <Text isInline secondary rightIndent="1">
              {t('Corrections:payments.card.table.tenant')}:
            </Text>
            {item.tenantId}
          </Text>
          <Text>
            <Text isInline secondary rightIndent="1">
              {t('Corrections:payments.card.table.paymentId')}:
            </Text>
            {item.paymentId}
          </Text>
          <ModalActions>
            <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
            <ModalSubmitButton
              onClick={methods.handleSubmit(handleSubmit)}
              disabled={!canRefund || isLoading}
            >
              {t('Corrections:payments.card.refundModal.submitButton')}
            </ModalSubmitButton>
          </ModalActions>
        </Modal>
      </FormProvider>
    );
  },
);

RefundModal.displayName = 'RefundModal';

const showRefundModal = (item: TPayKeeperPayment, isPartial?: boolean) =>
  showModal((closeModal) => (
    <RefundModal closeModal={closeModal} isPartial={isPartial} item={item} />
  ));

export {showRefundModal};
